'use client';

import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { UnauthQueryOptions } from '@/api';

import { RecentOrderGiftList } from './RecentOrderGiftList';

interface RecentOrderGiftsSectionProps {
  className?: string;
}

/**
 * 클라이언트에서 실시간 로딩 섹션
 */
export const RecentOrderGiftsSection = ({
  className,
}: RecentOrderGiftsSectionProps) => {
  const currentCarouselIndexRef = useRef(0);
  const dotIndicatorsRef = useRef<HTMLDivElement>(null);

  const {
    data: recentGiftProductList = [],
    isLoading: isGiftListLoading,
    isError: isGiftListError,
  } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-love/main/recent-gift-products'
    ](),
    //* 실시간성 유지하되 불필요한 중복 요청 방지
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 3,
    select: (data) => data.context?.contents ?? [],
  });

  //* 배열을 나누기
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }

    return chunks;
  };

  const chunkedList = chunkArray(recentGiftProductList, CHUNK_SIZE);
  const isShowCarousel = recentGiftProductList.length > CHUNK_SIZE;

  const shouldHideSection =
    (!isGiftListLoading && recentGiftProductList.length === 0) ||
    isGiftListError;

  if (shouldHideSection) {
    return null;
  }

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-16 px-40 content-container',
        'mobile-tablet:px-0',
        className,
      )}
    >
      <header
        className={cn(
          'relative w-full gap-2 flex-center',
          'mobile-tablet:justify-start mobile-tablet:gap-4 mobile-tablet:px-20',
        )}
      >
        <span
          className={cn(
            'mr-4 typo-h4',
            'mobile-tablet:mr-2 mobile-tablet:typo-body1',
          )}
        >
          📦
        </span>
        <h2
          className={cn(
            'bg-gradient-to-r from-O-500 to-[#F00] bg-clip-text font-800 text-transparent typo-h4',
            'mobile-tablet:font-700 mobile-tablet:typo-body1',
          )}
        >
          전국에서 실시간 주문중!
        </h2>
        {isShowCarousel ? (
          <div
            ref={dotIndicatorsRef}
            className={cn(
              'absolute bottom-0 right-12 flex translate-y-1/2 items-center gap-10 px-12 py-8',
              'mobile-tablet:bottom-1/2 mobile-tablet:right-20 mobile-tablet:translate-y-1/2 mobile-tablet:gap-6',
            )}
          >
            {chunkedList.map((_, chunkIndex) => (
              <div
                key={chunkIndex}
                data-index={chunkIndex}
                className={cn(
                  'size-4 rounded-pill bg-Gray-400 transition-all duration-300',
                  {
                    'bg-Gray-500 size-6 mobile-tablet:size-4': chunkIndex === 0,
                  },
                )}
              />
            ))}
          </div>
        ) : null}
      </header>

      <RecentOrderGiftList
        isLoading={isGiftListLoading}
        listItems={chunkedList}
        onSlideChange={(index) => {
          currentCarouselIndexRef.current = index;
          const container = dotIndicatorsRef.current;
          if (!container) return;
          const dots = container.children;
          for (let i = 0; i < dots.length; i++) {
            const dot = dots[i] as HTMLElement;
            if (i === index) {
              dot.className =
                'size-6 mobile-tablet:size-4 rounded-pill bg-Gray-500 transition-all duration-300';
            } else {
              dot.className =
                'size-4 rounded-pill bg-Gray-400 transition-all duration-300';
            }
          }
        }}
      />
    </section>
  );
};

const CHUNK_SIZE = 3;
